// Live feed for one pairs event (Paul, 31 Oct 2026). The browser LISTENS to Firestore directly - the
// event doc (status, round, clock, seating), the pairs (lobby list, presence), the standings, and the one
// view doc for my seat at my table (written by the server after every move; the rules let only that
// seat's uid read it). No long-polling: every table's moves arrive as Firestore pushes. Moves themselves
// go through the partnerHub function (phApi) as before.
// On localhost with "?as=<test id>" there is no Firebase sign-in, so the feed polls the function's evGet.
import { useCallback, useEffect, useRef, useState } from "react";
import { firebase } from "../../firebase/config";
import { ph, testUid } from "../PartnerHub/phApi";

const POLL_LOCAL_MS = 2500;
const PING_MS = 12000;

export function useEventFeed(eventId, uid) {
  const [state, setState] = useState({ event: null, pairs: [], standings: null, myPair: null, mySeat: null, view: null, admin: false, err: "", offset: 0 });
  const local = !!testUid();
  const tableRef = useRef(null);

  // ── localhost test mode: poll the function ──
  useEffect(() => {
    if (!local || !eventId) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const r = await ph("evGet", { eventId });
        if (!alive) return;
        tableRef.current = r.mySeat && r.mySeat.tableId ? r.mySeat.tableId : null;
        const offset = r.serverNow ? r.serverNow - Date.now() : 0;
        setState({ event: r.event, pairs: r.pairs, standings: r.standings, myPair: r.myPair, mySeat: r.mySeat, view: r.view, admin: r.admin, err: "", offset });
      } catch (e) {
        if (alive) setState((s) => ({ ...s, err: e.message }));
      }
    };
    load();
    const t = setInterval(load, POLL_LOCAL_MS);
    const on = () => load();
    window.addEventListener("ev-refresh", on);
    return () => { alive = false; clearInterval(t); window.removeEventListener("ev-refresh", on); };
  }, [eventId, local]);

  // ── live: Firestore listeners ──
  useEffect(() => {
    if (local || !eventId || !uid) return undefined;
    const db = firebase.firestore();
    const unsubs = [];
    unsubs.push(db.doc(`events/${eventId}`).onSnapshot((snap) => {
      if (!snap.exists) { setState((s) => ({ ...s, err: "That event isn't here any more." })); return; }
      const { seating, ...event } = snap.data();
      setState((s) => {
        const myPair = s.myPair;
        const seat = myPair && seating && seating[myPair.pairId] ? seating[myPair.pairId] : null;
        const mySeat = seat ? (seat.bye ? { bye: true } : { tableId: seat.tableId, tableNo: seat.tableNo, line: seat.line, seat: seat.seatOf[uid] }) : null;
        return { ...s, event: { id: snap.id, ...event, seating }, mySeat, err: "" };
      });
    }, (e) => setState((s) => ({ ...s, err: e.message }))));
    unsubs.push(db.collection(`events/${eventId}/pairs`).onSnapshot((q) => {
      const now = Date.now();
      const raw = q.docs.map((d) => d.data());
      const mine = raw.find((p) => p.uids.includes(uid) && p.status !== "withdrawn") || null;
      const pairs = raw.map((p) => ({
        pairId: p.pairId, names: p.uids.map((u) => shortName(p.names[u])), status: p.status,
        present: p.uids.map((u) => now - ((p.present || {})[u] || 0) < 90000), rank: p.rank || null, score: p.score || 0, pct: p.pct || 0,
      }));
      setState((s) => {
        const seating = s.event && s.event.seating;
        const seat = mine && seating && seating[mine.pairId] ? seating[mine.pairId] : null;
        const mySeat = seat ? (seat.bye ? { bye: true } : { tableId: seat.tableId, tableNo: seat.tableNo, line: seat.line, seat: seat.seatOf[uid] }) : null;
        return { ...s, pairs, myPair: mine, mySeat };
      });
    }, () => {}));
    unsubs.push(db.doc(`events/${eventId}/public/standings`).onSnapshot((snap) => {
      setState((s) => ({ ...s, standings: snap.exists ? snap.data() : null }));
    }, () => {}));
    return () => unsubs.forEach((u) => u());
  }, [eventId, uid, local]);

  // My table's view doc follows the seating.
  const tableId = state.mySeat && state.mySeat.tableId;
  const seat = state.mySeat && state.mySeat.seat;
  useEffect(() => {
    tableRef.current = tableId || null;
    if (local || !tableId || !seat) { if (!local) setState((s) => (s.view ? { ...s, view: null } : s)); return undefined; }
    const db = firebase.firestore();
    return db.doc(`eventTables/${tableId}/views/${seat}`).onSnapshot((snap) => {
      // The view carries the server's clock (`at`): the round clock and gaps are shown against it,
      // not the viewer's own clock.
      const d = snap.exists ? snap.data() : null;
      setState((s) => ({ ...s, view: d, offset: d && d.at && !snap.metadata.hasPendingWrites ? d.at - Date.now() : s.offset }));
    }, (e) => setState((s) => ({ ...s, err: e.message })));
  }, [tableId, seat, local]);

  // Admin flag (live mode) once.
  useEffect(() => {
    if (local || !uid) return;
    ph("evList").then((r) => setState((s) => ({ ...s, admin: !!r.admin }))).catch(() => {});
  }, [uid, local]);

  // I'm here: every PING_MS while the page is open (drives drop-out detection and the clock).
  const signedIn = !!uid || local;
  const myPairId = state.myPair ? state.myPair.pairId : null;
  useEffect(() => {
    // Re-armed when my pair or my table changes, so the first table-aware ping goes at once.
    if (!signedIn || !eventId) return undefined;
    let alive = true;
    // Pings carry on while the tab is in the background: "here" means the page is open, and a seat only
    // goes to the robot when the page is gone (closed, offline, asleep).
    const ping = () => { if (!alive) return; ph("evPing", { eventId, tableId: tableRef.current }).catch(() => {}); };
    ping();
    const t = setInterval(ping, PING_MS);
    const onShow = () => { if (!document.hidden) ping(); };
    document.addEventListener("visibilitychange", onShow);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onShow); };
  }, [eventId, signedIn, myPairId, tableId]);

  const refresh = useCallback(() => window.dispatchEvent(new Event("ev-refresh")), []);
  return { ...state, refresh };
}

export function shortName(n) {
  const p = String(n || "").trim().split(/\s+/).filter(Boolean);
  return p.length > 1 ? `${p[0]} ${p[p.length - 1][0].toUpperCase()}` : p[0] || "Player";
}

export const fmtWhen = (ms) => {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return new Date(ms).toString();
  }
};
export const fmtClock = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
export const FORMAT_LABEL = { swiss: "Swiss pairs", mitchell: "Mitchell" };
export const SCORING_LABEL = { imps: "IMPs (Butler)", mp: "Matchpoints" };
